import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { getAudioContext } from '@/shared/sound/audio-context';

const SESSION_KEY = 'start-screen-passed';

/**
 * Full screen splash screen overlay. This sessionStorage session is only rendered once:* - After it has been rendered and closed, sessionStorage[SESSION_KEY] = '1'，Subsequent refreshes/routing switches will no longer be displayed.* - Any protected route will be overwritten once when accessed for the first time (solve the problem of direct deep links from bookmarks to bypass the startup screen)*
 * Close trigger: keydown / pointerdown any. Actively call getAudioContext() when mounting to let the global* audio resume listener Get in position before the key is pressed, and audio is unlocked simultaneously when the key is pressed.*/
export default function StartScreenOverlay() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const switchBtnRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(SESSION_KEY) !== '1';
  });

  useEffect(() => {
    if (!visible) return;

    // Actively create audio context instances to ensure audio-context.ts Global resume listener // Registered to document before user presses any key getAudioContext();

    let triggered = false;

    const close = () => {
      if (triggered) return;
      triggered = true;
      sessionStorage.setItem(SESSION_KEY, '1');
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
      setVisible(false);
    };

    const onKey = (e: KeyboardEvent) => {
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return;
      close();
    };

    const onPointer = (e: PointerEvent) => {
      // exclude"Switch account"button itself——Pressing it should not incidentally close the overlay if (switchBtnRef.current?.contains(e.target as Node)) return;
      close();
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [visible]);

  const handleSwitchAccount = () => {
    // One step: clear login status + turn off overlay. Behind the scenes, RootSwitch has switched to the login page. // The user sees the form immediately without having to press any key again. logout();
    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(false);
  };

  const loggedIn = Boolean(token && user);
  const initial = user?.nickname?.charAt(0) ?? '?';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/85 backdrop-blur-2xl"
        >
          {loggedIn ? (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.5, ease: 'easeOut' }}
              className="flex flex-col items-center"
            >
              {/* avatar*/}
              <div
                className="relative h-[120px] w-[120px] rounded-full overflow-hidden ring-2 ring-primary/40"
                style={{ boxShadow: '0 0 80px rgba(251,191,36,0.35), 0 0 24px rgba(251,191,36,0.5) inset' }}
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.nickname} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary/80 to-amber-600 text-5xl font-game text-background">
                    {initial}
                  </div>
                )}
              </div>

              {/* welcome text*/}
              <p className="mt-8 text-xs tracking-[6px] text-muted-foreground uppercase">welcome back</p>
              <p className="mt-2 font-game text-3xl text-foreground">{user?.nickname}</p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.5, ease: 'easeOut' }}
              className="flex flex-col items-center"
            >
              <h1
                className="font-game text-[112px] leading-none text-primary"
                style={{ textShadow: '0 0 60px rgba(251,191,36,0.4), 0 4px 12px rgba(0,0,0,0.6)' }}
              >
                ♠ UNO
              </h1>
              <p className="mt-3 text-sm tracking-[8px] text-white/40 font-medium uppercase">
                Online Card Game
              </p>
            </motion.div>
          )}

          {/* Press any key to continue*/}
          <motion.p
            className="mt-20 text-sm tracking-[4px] uppercase text-muted-foreground"
            animate={{ opacity: [0.35, 1, 0.35] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            Press any key to continue</motion.p>

          {/* Switch account*/}
          {loggedIn && (
            <button
              ref={switchBtnRef}
              onClick={handleSwitchAccount}
              className="absolute bottom-12 bg-transparent border-none text-muted-foreground text-xs tracking-wider cursor-pointer hover:text-foreground transition-colors"
            >
              Not you? Switch account</button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
